import { useCallback, useEffect, useState } from 'react';
import { App, Skeleton, Typography } from 'antd';
import {
  CheckCircleFilled,
  MinusCircleOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { getEntitlements } from '@/api/capabilities';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import { EmptyState } from '@/components/EmptyState';

const { Text } = Typography;

/**
 * The hub's entitlement matrix over the EXISTING /v1/tenants/{hub}/entitlements endpoint. Shared by
 * the tenant-admin console (B1) and the integration-config UI (B3). The known India identity-rail
 * classes (REG-IN-40/41) are shown as a fixed matrix so an admin sees the whole surface — active AND
 * the ones they do NOT hold (the bind-gating story); any additional server-returned class renders
 * verbatim (forward-compat). Colour is always paired with a glyph + text (deuteranopia-safe).
 */
const KNOWN_CLASSES: { key: string; label: string; gates: string }[] = [
  { key: 'bank', label: 'Banking entity', gates: 'Bank-grade identity rails (regulated-bank status).' },
  {
    key: 's11a_notified',
    label: 'Section 11A notified',
    gates: 'Aadhaar Act §11A notified — Aadhaar eKYC / e-authentication.',
  },
  { key: 'swik_approved', label: 'SWIK approved', gates: 'Approved for the SWIK identity rail.' },
];

export function EntitlementMatrix() {
  const { message } = App.useApp();
  const [active, setActive] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    try {
      const res = await getEntitlements();
      setActive(Array.isArray(res.active) ? res.active : []);
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else message.error(n.message);
      setActive(null);
    } finally {
      setLoading(false);
    }
    // message stable from App context
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <Skeleton active paragraph={{ rows: 3 }} />;

  if (unavailable || active == null) {
    return (
      <EmptyState
        icon={<SafetyCertificateOutlined />}
        title="Entitlement matrix"
        description="The hub's active entitlement classes (bank / Section 11A / SWIK) — the India identity-rail gates that decide which providers can be bound. This lands once the capability-fabric registry is enabled for the hub."
        bullets={[
          'bank — bank-grade identity rails',
          's11a_notified — Aadhaar eKYC / e-auth',
          'swik_approved — the SWIK rail',
        ]}
      />
    );
  }

  const activeSet = new Set(active);
  const extras = active.filter((a) => !KNOWN_CLASSES.some((k) => k.key === a));

  return (
    <div>
      <div className="ol-entl-grid" role="table" aria-label="Entitlement matrix">
        <div className="ol-entl-grid__head" role="row">
          <span role="columnheader">Entitlement class</span>
          <span role="columnheader">Status</span>
          <span role="columnheader">Gates</span>
        </div>
        {KNOWN_CLASSES.map((c) => {
          const on = activeSet.has(c.key);
          return (
            <div className="ol-entl-grid__row" role="row" key={c.key}>
              <span role="cell">
                <Text strong style={{ fontSize: 12.5 }}>
                  {c.label}
                </Text>
                <div className="ol-mono" style={{ fontSize: 10.5, color: 'var(--ol-text-tertiary)' }}>
                  {c.key}
                </div>
              </span>
              <span role="cell">
                {on ? (
                  <span
                    style={{ color: 'var(--ol-positive)', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600 }}
                  >
                    <CheckCircleFilled aria-hidden /> Active
                  </span>
                ) : (
                  <span
                    style={{ color: 'var(--ol-text-tertiary)', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}
                  >
                    <MinusCircleOutlined aria-hidden /> Inactive
                  </span>
                )}
              </span>
              <span role="cell" style={{ color: 'var(--ol-text-secondary)', fontSize: 12 }}>
                {c.gates}
              </span>
            </div>
          );
        })}
        {extras.map((k) => (
          <div className="ol-entl-grid__row" role="row" key={k}>
            <span role="cell">
              <Text strong className="ol-mono" style={{ fontSize: 12 }}>
                {k}
              </Text>
            </span>
            <span role="cell">
              <span
                style={{ color: 'var(--ol-positive)', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600 }}
              >
                <CheckCircleFilled aria-hidden /> Active
              </span>
            </span>
            <span role="cell" style={{ color: 'var(--ol-text-tertiary)', fontSize: 12 }}>
              Additional entitlement (server-returned).
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
